import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Address as AddressCac, ContentItem, KeyValue, WidgetOptions } from "@ews/cac";
import { LocationDetailsSession } from "../../api/models/locationDetailsSession";
import { PlacardEditionRequest } from "../../api/models/placardEdit";
import { ApiErrorCode, CAC_CONTENT_TRANSLATION, CAC_SUPPORTED_COUNTRY, CONTINENTAL_REGIONS, COUNTRY_LANGUAGE, ServiceURLConstants } from "../../constants/upplm.constants";
import { GetLocationsRequest } from "../../interface/upplm.locations";
import { Address } from "../../interface/upplm.placardInformation";
import { TranslateDataPipe } from "../../pipes/translation.pipe";
import { UpsCommonService } from "../upplm.common.service";
import { CommonUserAttributesService } from "../upplm.commonUserAttributes.service";
import { ConfigurationService } from "../upplm.configuration.service";
import { StoredDataService } from "../upplm.storedData.service";


@Injectable()

export class LocationDetailsHelperService {
    globalRegionCountry: string = 'NA';

    constructor(private _configurationService: ConfigurationService,
        private _commonUserAttributesService: CommonUserAttributesService,
        private _commonService: UpsCommonService,
        private _translateData: TranslateDataPipe,
        private _httpclient: HttpClient,
        private _storedDataService: StoredDataService) { }

    prepareLocationDetailsForEdit(addressData: Address, companyName: string, clientKey: string): LocationDetailsSession {
        let locationDetails = new LocationDetailsSession();
        locationDetails.companyName = companyName;
        locationDetails.address1 = addressData.addressLine1;
        locationDetails.countryKey = addressData.countryCode;
        locationDetails.postal = addressData.postalCode;
        locationDetails.city = addressData.cityTown;
        locationDetails.state = addressData.stateProvince;
        locationDetails.clientKey = clientKey;
        return locationDetails;
    }

    getLocationDetailsFromCacAddress(addressData: AddressCac, clientKey: string): LocationDetailsSession {
        let locationDetails = new LocationDetailsSession();
        locationDetails.address1 = addressData.address1
        locationDetails.countryKey = addressData.country.key;
        locationDetails.postal = addressData.postal;
        locationDetails.city = addressData.city;
        locationDetails.state = addressData.state.key;
        locationDetails.clientKey = clientKey;
        return locationDetails;
    }

    isEmeaCountry(): boolean {
        if ((COUNTRY_LANGUAGE.find(x => x.key === this._commonService.locale_country).region) === CONTINENTAL_REGIONS.EMEA) {
            return true;
        }
        return false;
    }

    getTranslatedCountryValues(): KeyValue[] {
        let result: KeyValue[] = [];
        CAC_SUPPORTED_COUNTRY.forEach(country => {
            let countryVal = new KeyValue();
            countryVal.key = country.key;
            countryVal.value = this._translateData.transform(country.value);
            result.push(countryVal);
        }, this);
        return result;
    }

    getCACOptions(): WidgetOptions {
        let cacOptions: WidgetOptions = new WidgetOptions();
        const isEmea = this.isEmeaCountry();

        cacOptions.content = {
            contentItems: this.getCACTranslatedContent(),
            errorPrefixContentItem: new ContentItem()
        };

        cacOptions.countries = this.getTranslatedCountryValues();
        cacOptions.locale = this._commonService.locale;
        cacOptions.streetLevelSuggestions = true;
        cacOptions.validateIntl = true;
        cacOptions.postalFirstCountries = ["US", "CA", "DE", "FR", "IT", "DE", "GB", "ES"]
        cacOptions.address.country.key = this._commonService.locale_country ? this._commonService.locale_country : "US";
        cacOptions.address.country.value = COUNTRY_LANGUAGE.find(x => x.key === this._commonService.locale_country).value;
        //Show Fields
        cacOptions.showCountry = function () {
            return true;
        }
        cacOptions.showName = function () {
            return true;
        }
        cacOptions.showAddress1 = function () {
            return true;
        }
        cacOptions.showCity = function () {
            return true;
        }
        cacOptions.showState = function () {
            return !isEmea;
        }
        cacOptions.showStateInput = function () {
            return false;
        }
        cacOptions.showPostal = function () {
            return true;
        }
        //Hidden Fields
        cacOptions.showAddress2 = function () {
            return false;
        }
        cacOptions.showAddress3 = function () {
            return false;
        }
        cacOptions.showContactName = function () {
            return false;
        }
        cacOptions.showIsResidential = function () {
            return false;
        }
        cacOptions.showNickname = function () {
            return false;
        }
        cacOptions.showPhone = function () {
            return false;
        }
        cacOptions.showPhoneType = function () {
            return false;
        }
        cacOptions.showExtension = function () {
            return false;
        }
        cacOptions.showEmail = function () {
            return false;
        }
        cacOptions.showLocationId = function () {
            return false;
        }
        cacOptions.showFax = function () {
            return false;
        }
        //Required Fields
        cacOptions.countryRequired = function () {
            return true;
        }
        cacOptions.nameRequired = function () {
            return true;
        }
        cacOptions.address1Required = function () {
            return true;
        }
        cacOptions.cityRequired = function () {
            return true;
        }
        cacOptions.stateRequired = function () {
            return !isEmea;
        }
        cacOptions.postalRequired = function () {
            return true;
        }
        //Non Required fields
        cacOptions.contactNameRequired = function () {
            return false;
        };
        cacOptions.phoneRequired = function () {
            return false;
        };
        cacOptions.emailRequired = function () {
            return false;
        };
        cacOptions.address1MaxLength = function () {
            return 50;
        }

        return cacOptions;
    }

    private getCACTranslatedContent(): any[] {
        let cacTranslatedContent: any[] = [];
        CAC_CONTENT_TRANSLATION.forEach(cacElement => {
            cacTranslatedContent.push({ 'key': cacElement.cacKey, 'value': this._translateData.transform(cacElement.key) });
        });
        return cacTranslatedContent;
    }

    createGetLocationsRequest(editedAddress: AddressCac) {
        let getLocationsRequest = new GetLocationsRequest();
        getLocationsRequest.address.CountryCode = editedAddress.country.key;
        getLocationsRequest.address.AddressLine1 = editedAddress.address1;
        getLocationsRequest.address.CityTown = editedAddress.city;
        /*getLocationsRequest.address.StateProvince = editedAddress.state.key;*/
        getLocationsRequest.address.StateProvince = this.getState(editedAddress.country.key, editedAddress.state.key);
        getLocationsRequest.address.PostalCode = editedAddress.postal;
        return getLocationsRequest;
    }

    getState(countryCode: string, stateKey: string): string {
        const globalRegion = this._commonService.getRegionCountry(countryCode);
        switch (globalRegion) {
            case CONTINENTAL_REGIONS.NA:
                return stateKey;
            case CONTINENTAL_REGIONS.EMEA:
                return '';
            default:
                return '';
        }
    }

    async getLocationResponse(editedAddress: AddressCac) {
        return await this._httpclient.post<any>(this._commonService.createURL(ServiceURLConstants.GET_LOCATIONS, ApiErrorCode.GET_LOCATIONS), this.createGetLocationsRequest(editedAddress)).toPromise();
    }

    locationHasChanges(editedAddress: AddressCac, currentValue: LocationDetailsSession): boolean {
        if (editedAddress.address1.toLowerCase() !== currentValue.address1.toLowerCase()
            || editedAddress.city.toLowerCase() !== currentValue.city.toLowerCase()
            || editedAddress.state.key !== currentValue.state
            || editedAddress.country.key !== currentValue.countryKey
            || editedAddress.postal !== currentValue.postal) {
            return true;
        } else {
            return false;
        }
    }

    prepareSaveEditionRequest(editedAddres: AddressCac, companyName: string): PlacardEditionRequest {
        let placardEditionRequest = new PlacardEditionRequest();
        let locationDetails = new Address();
        locationDetails.countryCode = editedAddres.country.key;
        locationDetails.addressLine1 = editedAddres.address1;
        locationDetails.cityTown = editedAddres.city;
        /*locationDetails.stateProvince = editedAddres.state.key;*/
        locationDetails.stateProvince = this.getState(editedAddres.country.key, editedAddres.state.key);
        locationDetails.postalCode = editedAddres.postal;
        placardEditionRequest.address = locationDetails;
        placardEditionRequest.companyName = companyName;
        return placardEditionRequest;
    }
}